import { useState, useEffect } from 'react';
import { compose } from 'redux';
import { batch, connect, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { AppStoreState } from 'types';
import { getCurrentWorkbook } from 'state/modules/entity/selectors/workbook-selectors';
import { saveClickAwayAccess, getUser } from 'state/modules/user/thunks';
import { getUsersWorkbooks, getPageDetails } from 'state/modules/entity/thunks';
import { createEvent } from 'api';
import { UserState } from 'state/modules/user';
import { getNewLink } from 'util/workbook-util';
import { BOOK_CODE_DESCRIPTIONS, TEACHER_GUIDE_DESCRIPTIONS } from 'types';

type Props = {
  user: UserState;
  workbook: any;
  workbooks: { [bookId: number]: any };
  isAuthenticated: boolean;
  saveClickAwayAccess: (bookCode: string, edition: number) => void;
  getUsersWorkbooks: () => void;
  getPageDetails: (bookCode: number, pageNumber: string | number, isZoom?: boolean | null) => void;
  getUser: any;
};

const handle2018 = edition => (edition === '2018' ? '2022' : edition);

const ExternalLink = (props: Props) => {
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const { bookCode, edition, pageNumber } = params;

  const currentWorkbook = useSelector(state => getCurrentWorkbook(state, params));

  useEffect(() => {
    handleClickAway();
  }, []);

  const handleClickAway = async () => {
    const { saveClickAwayAccess, user } = props;

    if (!user.isAuthenticated) {
      setRedirectPath('/home');
      return;
    }
    await batch(async () => {
      await saveClickAwayAccess(bookCode ?? '', handle2018(edition));
      await props.getUser();
    });

    doRedirect();
  };

  const doRedirect = async () => {
    const { user } = props;
    const license = user.licenses.find(
      l => l.productCurriculumId === currentWorkbook.products?.[0].productCurriculumId
    );

    let redirectPath;

    //If user has trial or full license, slice off /ext and send them to the rest of the path on the books route
    if (license) {
      redirectPath = '/books' + location.pathname.slice('/ext'.length);
    }
    //no license, send them to the clickAways page
    else {
      let bookCodeStudent = bookCode ?? '';
      const BOOK_CODES = Object.keys(BOOK_CODE_DESCRIPTIONS);
      const VALID_BOOK_CODES = BOOK_CODES.concat(Object.keys(TEACHER_GUIDE_DESCRIPTIONS));
      if (!VALID_BOOK_CODES.includes(bookCodeStudent)) {
        redirectPath = '/404';
      } else {
        if (bookCodeStudent.startsWith('TG')) {
          bookCodeStudent = bookCode?.slice(2) ?? '';
        }

        redirectPath = getNewLink(
          bookCodeStudent ?? '',
          handle2018(edition?.toString()),
          'clickAways'
        );
      }
    }

    const values = queryString.parse(location.search);

    createEvent('externalAccess', {
      path: location.pathname,
      bookCode,
      edition: handle2018(edition),
      ...(pageNumber && { pageNumber: pageNumber }),
      ...(values.source && { source: values.source })
    });

    setRedirectPath(redirectPath);
  };

  if (!redirectPath) return null;

  navigate({
    pathname: redirectPath
    // state: {
    //   referrer: props.location,
    //   code: props.match.params.bookCode,
    //   workbookParams: props.match.params
    // }
  });
};

const mapStateToProps = (state: AppStoreState) => ({
  workbook: state.entity.workbooksById,
  workbooks: state.entity.workbooksById,
  user: state.user
});

const withRedux = connect(mapStateToProps, {
  saveClickAwayAccess,
  getUsersWorkbooks,
  getPageDetails,
  getUser
});

export default compose<any>(withRedux)(ExternalLink);
